import { Component } from '@angular/core';
import { AbstractControl, UntypedFormArray } from '@angular/forms';
import { ItemReorderEventDetail } from '@ionic/angular';
import { createForm, patchForm } from '../../config';
import { BaseFormControl } from '../base-control';
import { FormArrayField } from '../form-field';

@Component({
    standalone: false,
    template: ''
})
export class BaseFormArrayComponent extends BaseFormControl {
    field: FormArrayField;
    header: string;

    get fields() {
        return this.parentFormGroup.get(this.field.name) as UntypedFormArray;
    }

    /**
     * Which row is open, by position in the array.
     *
     * Deliberately NOT the item's id. Open/closed is UI state that lasts until the editor is
     * closed, and keying it on a persisted field meant every row saved before that field existed -
     * id null in the database - compared equal to every other and to a closed editor, so opening
     * one row opened all of them. An index is always present, always unique, and needs nothing
     * stored. Items keep their ids; those are domain identity (EventListSlot.id is what list
     * submissions match on) and are simply not what decides this.
     */
    selectedItemIndex: number | null = null;
    fixNumericalIds = true;

    ngAfterViewInit() {
        if (this.field.fixNumericalIds) {
            let nextId = this.fields.value.map((x) => x.id).sort((a, b) => b - a)[0] + 1 || 0;
            setTimeout(() => {
                let newValue = this.fields.value.map((x, i, all) => {
                    if (x.id === undefined || x.id === null || isNaN(x.id)) {
                        x.id = nextId;
                        nextId++;
                    }
                    return x;
                });
                this.fields.setValue(newValue);
            }, 1000);
        }
    }

    close() {
        this.selectedItemIndex = null;
    }

    addItem(item: any = {}) {
        let newId = this.fields.value.map((x) => x.id).sort((a, b) => b - a)[0] + 1 || 0;

        // Duplicating passes the source item, and its values win — only a genuinely
        // new item is seeded from its siblings.
        const isDuplicate = Object.keys(item ?? {}).length > 0;
        const seed =
            !isDuplicate && this.field.newItemDefaults
                ? this.field.newItemDefaults(this.fields.value ?? [], this.parentFormGroup.root.value)
                : {};

        let newItem: any = { ...seed, ...item, id: newId };
        let newValue = [...this.fields.value, newItem];

        let newForm = createForm(this.field.fields);
        patchForm(newForm, this.field.fields, newItem);
        this.fields.push(newForm);

        // this.fields.patchValue(newValue);
        this.selectedItemIndex = this.fields.length - 1;
    }

    getItemHeaderColor(index: number) {
        return this.selectedItemIndex === index ? 'primary' : '';
    }

    isOpen(index: number): boolean {
        return this.selectedItemIndex === index;
    }

    showItem(e: KeyboardEvent | MouseEvent, control: AbstractControl, index: number) {
        if (e.altKey) {
            this.clickThrough(control?.value);
            return;
        }

        this.selectedItemIndex = this.selectedItemIndex === index ? null : index;
    }

    deleteItem(itemIndex: number) {
        this.fields.removeAt(itemIndex);
        // Every index after the removed one has shifted, so the open one no longer means what it
        // meant. Closing is the honest answer: the row being edited is the one just deleted.
        this.close();
    }

    doReorder(ev: CustomEvent<ItemReorderEventDetail>) {
        try {
            ev.stopPropagation(); // Prevents ion-reorder-group within another ion-reorder-group trying to reorder the parent
            let { from, to, complete } = ev.detail;
            let item = this.fields.at(from);
            this.fields.removeAt(from);
            this.fields.insert(to, item);
            // Positions have moved, so the open index points at a different row now.
            this.close();
            let items: any[] = this.fields.value;
            complete();
            this.fields.setValue(items);
        } catch (error) {
            console.error(error);
        }
    }

    clickThrough(item) {
        console.log(item);
        if (this.field.clickThrough) {
            this.field.clickThrough(item);
        }
    }
}
